import React from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { FaBook, FaGraduationCap, FaMapMarkerAlt, FaGlobe, FaFlask, FaMicroscope } from "react-icons/fa";
import "./StateSyllabus.css";
import { ENQUIRY_URL } from "../config";

const Cambridge = () => {
  const highlights = [
    {
      icon: "🏛️",
      title: "Available Since",
      value: "2012",
      description: "Sri Vani has offered Cambridge curriculum since 2012",
    },
    {
      icon: "🌍",
      title: "Affiliated To",
      value: "CAIE",
      description: "Cambridge Assessment International Education",
    },
    {
      icon: "🌐",
      title: "Global Reach",
      value: "160 Countries",
      description: "Recognised in 10,000+ schools across 160 countries",
    },
    {
      icon: "🏫",
      title: "Campus",
      value: "Rajajinagar",
      description: "Available at Rajajinagar Campus",
    },
  ];

  const curriculumLevels = [
    {
      level: "Cambridge Primary — Nursery to KG",
      icon: <FaBook />,
      subjects: [
        "Cornerstone 1",
        "Cornerstone 2",
        "Cornerstone 3",
      ],
    },
    {
      level: "Primary Program 1–2 — Grades 1 & 2",
      icon: <FaBook />,
      subjects: [
        "English",
        "Science & Mathematics",
        "Kannada / Sanskrit / Hindi / French",
        "ICT",
        "Global Perspectives",
        "Music & Dance",
        "Drawing & Craft",
      ],
    },
    {
      level: "Primary Program 3–4 — Grades 3 & 4",
      icon: <FaGraduationCap />,
      subjects: [
        "English & Social Studies",
        "Science & Mathematics",
        "Kannada / Sanskrit / Hindi / French",
        "ICT",
        "Global Perspectives",
        "Music & Dance",
        "Art & Craft",
        "Yoga",
      ],
    },
    {
      level: "Checkpoints 1–3 — Grades 5 to 8",
      icon: <FaGlobe />,
      subjects: [
        "English & Social Studies",
        "Science & Mathematics",
        "Sanskrit / Hindi / French",
        "Kannada",
        "ICT",
        "Global Perspectives",
        "Music & Dance",
        "Art & Craft",
        "Yoga",
      ],
    },
    {
      level: "IGCSE — Grades 9 & 10",
      icon: <FaMicroscope />,
      subjects: [
        "English & Social Studies",
        "Science & Mathematics",
        "Sanskrit / Hindi / French",
        "Kannada",
        "ICT",
        "Yoga",
      ],
    },
    {
      level: "AS & A Levels — Grades 11 & 12",
      icon: <FaFlask />,
      subjects: [
        "English",
        "Mathematics",
        "Physics",
        "Chemistry & Biology",
        "Design & Technology",
        "ICT",
      ],
    },
  ];

  const campus = {
    name: "Rajajinagar Campus",
    subtitle: "Ramavana",
    tagline: "A Burgeoning Campus that Vibrates Sentience.",
    image: "/images/state-syllabus/rajaji-nagar-campus.jpg",
    link: "/campuses/rajajinagar",
  };

  return (
    <div className="state-syllabus-page">
      {/* Page Header */}
      <section
        className="ss-page-header"
        style={{ backgroundImage: "url('/images/cambridge/cambridge-hero.jpg')" }}
      >
        <div className="ss-header-overlay" />
        <div className="container">
          <motion.div
            className="ss-header-content"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <p className="ss-header-tag">Cambridge Assessment International Education</p>
            <h1>Cambridge International</h1>
            <p className="ss-header-sub">160 Years of Excellence in Education</p>
          </motion.div>
        </div>
      </section>

      {/* Quick Highlights */}
      <section className="ss-highlights-section">
        <div className="container">
          <div className="ss-highlights-grid">
            {highlights.map((item, index) => (
              <motion.div
                key={index}
                className="ss-highlight-card"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <div className="ss-highlight-icon">{item.icon}</div>
                <div className="ss-highlight-value">{item.value}</div>
                <div className="ss-highlight-title">{item.title}</div>
                <div className="ss-highlight-desc">{item.description}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Emblem + Bullets Section */}
      <section className="ss-emblem-section">
        <div className="container">
          <div className="ss-emblem-row">
            <motion.div
              className="ss-emblem-col"
              initial={{ opacity: 0, x: -40 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <img
                src="/images/cambridge/cambridge-emblem.png"
                alt="Cambridge International Emblem"
                className="ss-emblem-img"
              />
            </motion.div>
            <motion.div
              className="ss-bullets-col"
              initial={{ opacity: 0, x: 40 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <ul className="ss-key-bullets">
                <li>Available at Sri Vani Since 2012</li>
                <li>Part of University of Cambridge</li>
                <li>In 10,000+ schools &amp; 160 countries</li>
                <li>Guaranteed Individual Attention</li>
                <li>Available at Rajajinagar Campus</li>
              </ul>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Description Section */}
      <section className="ss-description-section">
        <div className="container">
          <motion.div
            className="ss-description-content"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <p>
              Cambridge Assessment International Education (CAIE) is wholly owned
              by the University of Cambridge and brings over 160 years of experience
              in the education sector. The curriculum is followed in 10,000+ schools
              across 160 countries, combining international excellence with local
              cultural relevance.
            </p>
            <p>
              Students develop essential skills including problem solving, critical
              thinking, independent research, collaboration, and presenting arguments.
              The Assessment for Learning (AFL) methodology provides continuous
              feedback, enabling individualized attention and guiding each student's
              performance.
            </p>
            <div className="ss-desc-cta">
              <a
                href={ENQUIRY_URL}
                className="btn btn-secondary"
                target="_blank"
                rel="noopener noreferrer"
              >
                Apply Now
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Curriculum Section */}
      <section className="ss-subjects-section section">
        <div className="container">
          <motion.div
            className="ss-subjects-header"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="ss-section-title">Cambridge International Pathway</h2>
            <p className="ss-section-subtitle">
              A complete learning journey from Nursery through A Levels
            </p>
          </motion.div>

          <div className="cbse-curriculum-grid icse-curriculum-grid">
            {curriculumLevels.map((level, index) => (
              <motion.div
                key={index}
                className="ss-subject-block"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.08 }}
              >
                <div className="ss-subject-block-header">
                  {level.icon}
                  <h3>{level.level}</h3>
                </div>
                <ul className="ss-subject-list">
                  {level.subjects.map((sub, idx) => (
                    <li key={idx}>{sub}</li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Campus Section */}
      <section className="ss-campuses-section section">
        <div className="container">
          <motion.div
            className="ss-campuses-header"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <div className="ss-section-tag">
              <FaMapMarkerAlt /> Pursue at Rajajinagar Campus
            </div>
            <h2 className="ss-section-title">Our Cambridge Campus</h2>
          </motion.div>

          <div className="cbse-campus-center">
            <motion.div
              className="ss-campus-card cbse-single-campus"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
            >
              <div className="ss-campus-image">
                <img src={campus.image} alt={campus.name} />
                <div className="ss-campus-overlay">
                  <span>{campus.subtitle}</span>
                </div>
              </div>
              <div className="ss-campus-info">
                <h3>{campus.name}</h3>
                <p>{campus.tagline}</p>
                <Link to={campus.link} className="btn btn-outline ss-explore-btn">
                  Explore Campus
                </Link>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="ss-cta-section">
        <div className="container">
          <motion.div
            className="ss-cta-content"
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2>Begin Your Cambridge Journey at Sri Vani</h2>
            <p>
              Join a globally recognised curriculum that opens doors to universities
              and opportunities across 160 countries.
            </p>
            <div className="ss-cta-buttons">
              <a
                href={ENQUIRY_URL}
                className="btn btn-secondary"
                target="_blank"
                rel="noopener noreferrer"
              >
                Apply for Admission
              </a>
              <Link to="/contact" className="btn btn-outline-white">
                Contact Us
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Cambridge;
